import React, { useEffect, useRef } from 'react';

/**
 * Reusable modal for save / action feedback (success or error).
 *
 * @param {boolean} show - Whether to show the popup
 * @param {function} onClose - Callback when popup is closed
 * @param {'success'|'error'} variant - Visual style (default: success)
 * @param {string} title - Title text
 * @param {string} message - Main message text
 * @param {string} buttonText - Primary button label (default: OK)
 * @param {number} autoCloseDelay - Auto-close ms; 0 = disabled. Errors default to 0 when omitted.
 */
const SuccessPopup = ({
  show,
  onClose,
  variant = 'success',
  title,
  message,
  buttonText = 'OK',
  autoCloseDelay
}) => {
  const timeoutRef = useRef(null);
  const isError = variant === 'error';

  const resolvedTitle = title ?? (isError ? 'Something went wrong' : 'Success!');
  const resolvedDelay =
    autoCloseDelay !== undefined && autoCloseDelay !== null
      ? autoCloseDelay
      : isError
        ? 0
        : 5000;

  useEffect(() => {
    if (show && resolvedDelay > 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        onClose();
      }, resolvedDelay);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [show, resolvedDelay, onClose]);

  const handleDismiss = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    onClose();
  };

  if (!show) return null;

  const accent = isError ? '#c0392b' : '#27ae60';
  const icon = isError ? '✕' : '✓';

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}
        onClick={handleDismiss}
      >
        <div
          style={{
            background: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            position: 'relative',
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={handleDismiss}
            style={{
              position: 'absolute',
              top: '0.75rem',
              right: '0.75rem',
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: '#666',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f0f0f0';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <div
              style={{
                fontSize: '3rem',
                marginBottom: '1rem',
                color: accent,
                fontWeight: 'bold',
                lineHeight: 1
              }}
              aria-hidden="true"
            >
              {icon}
            </div>
            <h3
              style={{
                margin: '0 0 0.5rem 0',
                color: '#2c3e50',
                fontSize: '1.5rem'
              }}
            >
              {resolvedTitle}
            </h3>
            <p
              style={{
                margin: 0,
                color: '#666',
                fontSize: '1rem',
                lineHeight: '1.5'
              }}
            >
              {message}
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={handleDismiss}
              style={{
                backgroundColor: accent,
                color: 'white',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '0.92';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '1';
              }}
            >
              {buttonText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SuccessPopup;
